import { useMemo, useState, useCallback } from "react";
import { FileExplorerToolbar } from "./FileExplorerToolbar";
import { FileGallery } from "../file-gallery/FileGallery";
import { FileExplorerPagination } from "./FileExplorerPagination";
import { FileExplorerGalleryError } from "./FileExplorerGalleryError";
import { FileDetailsPane } from "../shared/FileDetailsPane";
import { FileExplorerActiveFilters } from "./FileExplorerActiveFilters";
import { FileExplorerFilters as FiltersPane } from "./FileExplorerFilters";
import { BulkEditAttributesDialog } from "./BulkEditAttributesDialog";
import { BulkAddTagsDialog } from "./BulkAddTagsDialog";
import { getTabView } from "./tab-views/TabViewRegistry";
import { useBulkFileDownload } from "../../hooks/useBulkFileDownload";
import { clearAllFileFilters } from "../../utils/fileUtils";
import type { ViewMode, FileItem, FileExplorerFilters } from "../../types";
import {
  countActiveFilters,
  extractAttributeOrder,
} from "../../utils/fileUtils";
import type { DisplayAttribute } from "@/features/admin/panels/categories/hooks/useCategoryAttributesDisplay";
import type { CategoryAttributeResponse } from "@/services/api";
import type { CategoryTab } from "@/services/api/categories/types";

interface FileExplorerContentProps {
  activeTab: string;
  tabs?: CategoryTab[];
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  isFiltersOpen: boolean;
  onFiltersToggle: () => void;
  onUploadOpen: () => void;
  filters: FileExplorerFilters;
  onFiltersChange: (filters: FileExplorerFilters) => void;
  allFiles: FileItem[];
  validSelectedFile: FileItem | null;
  matchedCategoryId?: string;
  categoryAttributes?: DisplayAttribute[];
  filterCategoryAttributes: CategoryAttributeResponse[];
  galleryError: Error | null;
  sortBy?: string;
  sortDirection?: "ASC" | "DESC";
  onFileSelect: (file: FileItem) => void;
  onSort: (column: string) => void;
  onImageClick: (file: FileItem) => void;
  onCloseDetails: () => void;
  onFileNavigate: (direction: "prev" | "next") => void;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalFiles: number;
  allowedPageSizes: number[];
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (items: number) => void;
  canDeleteFiles: boolean;
}

export function FileExplorerContent({
  activeTab,
  tabs = [],
  viewMode,
  onViewModeChange,
  isFiltersOpen,
  onFiltersToggle,
  onUploadOpen,
  filters,
  onFiltersChange,
  allFiles,
  validSelectedFile,
  matchedCategoryId,
  categoryAttributes = [],
  filterCategoryAttributes,
  galleryError,
  sortBy,
  sortDirection,
  onFileSelect,
  onSort,
  onImageClick,
  onCloseDetails,
  onFileNavigate,
  currentPage,
  totalPages,
  itemsPerPage,
  totalFiles,
  allowedPageSizes,
  onPageChange,
  onItemsPerPageChange,
  canDeleteFiles,
}: FileExplorerContentProps) {
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(
    new Set()
  );
  const [isAttributesDialogOpen, setIsAttributesDialogOpen] = useState(false);
  const [isTagsDialogOpen, setIsTagsDialogOpen] = useState(false);
  const { isDownloading: isDownloadingAll, downloadFiles } = useBulkFileDownload();

  const activeFiltersCount = useMemo(
    () => countActiveFilters(filters),
    [filters]
  );

  const selectedFiles = useMemo(
    () => allFiles.filter((f) => selectedFileIds.has(f.id)),
    [allFiles, selectedFileIds]
  );

  const attributeOrder = useMemo(
    () => extractAttributeOrder(allFiles),
    [allFiles]
  );

  const handleMultiSelect = useCallback((file: FileItem, checked: boolean) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(file.id);
      } else {
        next.delete(file.id);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedFileIds(new Set(allFiles.map((f) => f.id)));
      } else {
        setSelectedFileIds(new Set());
      }
    },
    [allFiles]
  );

  const handleFileSelect = useCallback(
    (file: FileItem) => {
      setSelectedFileIds(new Set());
      onFileSelect(file);
    },
    [onFileSelect]
  );

  const activeTabConfig = useMemo(() => {
    if (!activeTab || tabs.length === 0) {
      return null;
    }
    return tabs.find((tab) => tab.value === activeTab) ?? null;
  }, [tabs, activeTab]);

  const viewType = useMemo(() => {
    return activeTabConfig?.viewType ?? "files";
  }, [activeTabConfig?.viewType]);

  const customView = useMemo(() => {
    return getTabView(viewType, {
      categoryId: matchedCategoryId,
      tabId: activeTabConfig?.id,
    });
  }, [viewType, matchedCategoryId, activeTabConfig?.id]);

  if (customView) {
    return <>{customView}</>;
  }

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden min-w-0 w-full max-w-full">
      <div className="relative flex flex-1 flex-col overflow-hidden min-w-0 min-h-0 w-full max-w-full">
        <FileExplorerToolbar
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          isFiltersOpen={isFiltersOpen}
          onFiltersToggle={onFiltersToggle}
          onUploadOpen={onUploadOpen}
          activeFiltersCount={activeFiltersCount}
          selectedFilesCount={selectedFileIds.size}
          onEditAttributes={() => setIsAttributesDialogOpen(true)}
          onEditTags={() => setIsTagsDialogOpen(true)}
          onDownloadAll={() => downloadFiles(selectedFiles)}
          isDownloadingAll={isDownloadingAll}
        />

        {isFiltersOpen && (
          <div className="border-b border-grid-200 bg-white px-4 py-3 shrink-0 overflow-hidden animate-in slide-in-from-top-2 fade-in duration-200">
            <FiltersPane
              filters={filters}
              onFiltersChange={onFiltersChange}
              onClearFilters={() => onFiltersChange(clearAllFileFilters())}
              files={allFiles}
              categoryAttributes={filterCategoryAttributes}
            />
          </div>
        )}

        <FileExplorerActiveFilters
          filters={filters}
          onFiltersChange={onFiltersChange}
        />

        <div className="flex-1 overflow-auto bg-grid-50 p-3 min-h-0 min-w-0 w-full max-w-full pb-16">
          {galleryError ? (
            <FileExplorerGalleryError />
          ) : (
            <FileGallery
              files={allFiles}
              viewMode={viewMode}
              onFileSelect={handleFileSelect}
              selectedFile={validSelectedFile}
              selectedFileIds={selectedFileIds}
              onMultiSelect={handleMultiSelect}
              onSelectAll={handleSelectAll}
              onSort={onSort}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onImageClick={onImageClick}
              attributeOrder={attributeOrder}
              categoryAttributes={categoryAttributes}
            />
          )}
        </div>

        <FileExplorerPagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalFiles={totalFiles}
          onPageChange={onPageChange}
          onItemsPerPageChange={onItemsPerPageChange}
          allowedPageSizes={allowedPageSizes}
        />
      </div>

      {validSelectedFile && (
        <FileDetailsPane
          key={matchedCategoryId || "no-category"}
          file={validSelectedFile}
          files={allFiles}
          onClose={onCloseDetails}
          onNavigate={onFileNavigate}
          onImageClick={onImageClick}
          canDelete={canDeleteFiles}
          categoryId={matchedCategoryId}
          attributeOrder={attributeOrder}
          categoryAttributes={categoryAttributes}
        />
      )}

      {matchedCategoryId && (
        <>
          <BulkEditAttributesDialog
            open={isAttributesDialogOpen}
            onOpenChange={setIsAttributesDialogOpen}
            selectedFiles={selectedFiles}
            categoryId={matchedCategoryId}
            categoryAttributes={categoryAttributes}
          />
          <BulkAddTagsDialog
            open={isTagsDialogOpen}
            onOpenChange={setIsTagsDialogOpen}
            selectedFiles={selectedFiles}
          />
        </>
      )}
    </div>
  );
}
